/**
 * Theme Token Catalog — single source of truth for the runtime theming system (v2).
 *
 * Layer model (raw → specific):
 *   1. BASE COLORS — 8 admin-picked seeds per theme (c1…c6 + neutral + error).
 *      Each derives ONE M3 accent family → --accent-{key}-{role} (base/on/container/…),
 *      plus a tonal ramp (--palette-{key}-{stop}). One shared SURFACE set (neutral/
 *      outline/background) is derived from the theme's surfaceColor base → --surface, …
 *   2. SEMANTIC    — role tokens (--color-*, --shape-*, --text-*, --layout-*) wired to
 *      the accent/surface vars in _semantic.scss. Components consume only these.
 *
 * The admin edits: the 8 base colors, a per-category accent base color (which base
 * color a component group's bg/fg/hover follow), the surface source colour, typography
 * and shape. light/dark is the only runtime mode axis (via HCT isDark); other "modes"
 * become separate theme records. A theme variant = same structure, different base colors.
 */

export type TokenKind = 'color' | 'length' | 'fontFamily' | 'number' | 'lineHeight' | 'motion';

export type TokenCategory =
    | 'surface'
    | 'header'
    | 'footer'
    | 'tablist'
    | 'list-item'
    | 'expansion-list'
    | 'list-position'
    | 'tooltip'
    | 'chat'
    | 'task'
    | 'notification'
    | 'button-action'
    | 'button-navigation'
    | 'button-neutral'
    | 'button-mini-fab'
    | 'button-flat'
    | 'button-icon'
    | 'button-disabled'
    | 'control'
    | 'body'
    | 'generic'
    | 'input'
    | 'link'
    | 'imagebox'
    | 'typography-family'
    | 'typography-scale'
    | 'shape'
    | 'layout';

export type TokenEntry = {
    cssVar: string;
    category: TokenCategory;
    label: string;
    description: string;
    default: string;
    kind: TokenKind;
};

// ─── BASE COLORS ────────────────────────────────────────────────────────────
// 11 seeds per theme. c1…c6 are free accent slots; neutral + the status colors (info, success,
// warning, error) are shared across all contexts. Each expands to a --palette-{key}-{stop} ramp at
// runtime. Status colors follow the usual convention (info blue, success green, warning amber, error
// red) and carry meaning — use them only for status (notifications, state markers), not as decoration.

export const BASE_COLOR_KEYS = ['c1', 'c2', 'c3', 'c4', 'c5', 'c6', 'neutral', 'info', 'success', 'warning', 'error'] as const;
export type BaseColorKey = typeof BASE_COLOR_KEYS[number];

/** A context seed references one of the base colors. */
export type ColorRef = BaseColorKey;

export type BaseColors = Record<BaseColorKey, string>; // hex per key

// ─── ACCENT FAMILIES + SURFACE ROLES ────────────────────────────────────────
// v2.1: the per-context layer is gone. Each base color derives ONE accent family
// (theme.generator buildAccentFamily) — the seed, its contrast on-color, and a
// container/hover tier. Neutral-ish surfaces + outline come from a single shared
// SURFACE set derived from the theme's `surfaceColor` base. So a colour token now
// resolves to either an accent role (`--accent-{key}-{role}`) of some base color,
// or a shared surface role (`--{role}`).

/** Role suffixes a base color emits as an accent family. 'base' = the seed itself. */
export const ACCENT_ROLE_KEYS = ['base', 'on', 'container', 'container-high', 'on-container'] as const;
export type AccentRole = typeof ACCENT_ROLE_KEYS[number];

/**
 * Accent roles whose tone is driven by the hover/container intensity (the *-container
 * tier and its contrast on-color). A per-category hover override only re-tints tokens
 * that resolve to one of these; base/on are unaffected (hover doesn't move them).
 */
export const HOVER_AFFECTED_ROLES: ReadonlySet<AccentRole> =
    new Set<AccentRole>(['container', 'container-high', 'on-container']);

/** Shared neutral/surface roles, derived once from the theme's surfaceColor base. */
export const SURFACE_ROLE_KEYS = [
    'surface', 'on-surface', 'surface-variant', 'on-surface-variant',
    'surface-container-lowest', 'surface-container', 'surface-container-high', 'background', 'on-background',
    'outline', 'outline-variant',
] as const;
export type SurfaceRole = typeof SURFACE_ROLE_KEYS[number];

/** CSS var for an accent role of a base color: base → --accent-c1, else --accent-c1-on. */
export function accentVar(key: BaseColorKey, role: AccentRole): string {
    return role === 'base' ? `var(--accent-${key})` : `var(--accent-${key}-${role})`;
}

/** CSS var for a shared surface role: --surface, --on-surface, --outline, … */
export function surfaceVar(role: SurfaceRole): string {
    return `var(--${role})`;
}

/**
 * Default accent base color per category: which of the 8 base colors a category's
 * accent-typed tokens (bg/fg/hover) follow out of the box. Mirrors the pre-v2.1
 * context seed assignment (every group → c1 primary; links → c3 tertiary). The admin
 * overrides this per category via Theme.accentColors; categories absent here are
 * surface-only (no accent) by default.
 */
export const CATEGORY_ACCENT: Partial<Record<TokenCategory, BaseColorKey>> = {
    'button-action':     'c1',
    'button-navigation': 'c1',
    'button-neutral':    'c1',
    'button-mini-fab':   'c1',
    'button-flat':       'c1',
    'button-icon':       'c1',
    'list-item':         'c1',
    'link':              'c3',
};

/**
 * Categories whose hover is an opacity fill over a transparent (ghost) background
 * rather than a filled accent. Retained as static metadata for the component SCSS;
 * the accent container is derived once per base color (no per-category baking).
 */
export const GHOST_HOVER_CATEGORIES: ReadonlySet<TokenCategory> = new Set<TokenCategory>([
    'button-navigation', 'button-neutral', 'button-mini-fab', 'button-flat', 'button-icon',
]);

// ─── PRIMITIVES (shape scale; not color) ────────────────────────────────────
// Color ramps + context roles are generated at runtime by ThemeService. The
// shape scale below is a static factory default referenced by semantic tokens.

const PRIMITIVES = {
    'radius.none':  { cssVar: '--radius-none',  default: '0',      kind: 'length' as const },
    'radius.sm':    { cssVar: '--radius-sm',    default: '0.25em', kind: 'length' as const },
    'radius.md':    { cssVar: '--radius-md',    default: '0.4em',  kind: 'length' as const },
    'radius.lg':    { cssVar: '--radius-lg',    default: '0.8em',  kind: 'length' as const },
    'radius.pill':  { cssVar: '--radius-pill',  default: '9999px', kind: 'length' as const },

    'border-width.none':   { cssVar: '--border-width-none',   default: '0',     kind: 'length' as const },
    'border-width.thin':   { cssVar: '--border-width-thin',   default: '1px',   kind: 'length' as const },
    'border-width.normal': { cssVar: '--border-width-normal', default: '2px',   kind: 'length' as const },
    'border-width.thick':  { cssVar: '--border-width-thick',  default: '0.2em', kind: 'length' as const },
};

// ─── LEGACY ROLE → NEW VAR MAPPING ──────────────────────────────────────────
// The token defaults below are still written as ctx('<role>') for readability and
// to keep the diff small, but a role now maps straight to the new layer: accent
// roles resolve to the role's default base color (primary→c1, secondary→c2,
// tertiary→c3, error→error), neutral roles to the shared surface set. The admin
// re-points a token by changing the *base color* of its category (Theme.accentColors)
// — parseTokenColor (below) recovers a token's (key, role) so the runtime can swap it.
const _ROLE_VAR: Record<string, string> = {
    primary: accentVar('c1', 'base'), 'on-primary': accentVar('c1', 'on'),
    'primary-container': accentVar('c1', 'container'), 'primary-container-high': accentVar('c1', 'container-high'),
    'on-primary-container': accentVar('c1', 'on-container'),
    secondary: accentVar('c2', 'base'), 'on-secondary': accentVar('c2', 'on'),
    'secondary-container': accentVar('c2', 'container'), 'secondary-container-high': accentVar('c2', 'container-high'),
    'on-secondary-container': accentVar('c2', 'on-container'),
    tertiary: accentVar('c3', 'base'), 'on-tertiary': accentVar('c3', 'on'),
    'tertiary-container': accentVar('c3', 'container'), 'tertiary-container-high': accentVar('c3', 'container-high'),
    'on-tertiary-container': accentVar('c3', 'on-container'),
    info: accentVar('info', 'base'), 'on-info': accentVar('info', 'on'),
    'info-container': accentVar('info', 'container'), 'on-info-container': accentVar('info', 'on-container'),
    success: accentVar('success', 'base'), 'on-success': accentVar('success', 'on'),
    'success-container': accentVar('success', 'container'), 'on-success-container': accentVar('success', 'on-container'),
    warning: accentVar('warning', 'base'), 'on-warning': accentVar('warning', 'on'),
    'warning-container': accentVar('warning', 'container'), 'on-warning-container': accentVar('warning', 'on-container'),
    error: accentVar('error', 'base'), 'on-error': accentVar('error', 'on'),
    'error-container': accentVar('error', 'container'), 'on-error-container': accentVar('error', 'on-container'),
    surface: surfaceVar('surface'), 'on-surface': surfaceVar('on-surface'),
    'surface-variant': surfaceVar('surface-variant'), 'on-surface-variant': surfaceVar('on-surface-variant'),
    'surface-container-lowest': surfaceVar('surface-container-lowest'),
    'surface-container': surfaceVar('surface-container'), 'surface-container-high': surfaceVar('surface-container-high'),
    background: surfaceVar('background'), 'on-background': surfaceVar('on-background'),
    outline: surfaceVar('outline'), 'outline-variant': surfaceVar('outline-variant'),
};
const ctx = (role: string): string => _ROLE_VAR[role] ?? role;

/**
 * Recover the (base color, accent role) or surface role behind a token's resolved
 * colour var, so the runtime can re-point it at a different base color. Literals
 * (transparent/inherit/overlay) and non-colour vars → null (not re-pointable).
 */
export function parseTokenColor(value: string):
    | { kind: 'accent'; key: BaseColorKey; role: AccentRole }
    | { kind: 'surface'; role: SurfaceRole }
    | null {
    const __v = value.trim();
    const __a = /^var\(--accent-([a-z0-9]+?)(?:-(on-container|container-high|container|on))?\)$/.exec(__v);
    if (__a) return { kind: 'accent', key: __a[1] as BaseColorKey, role: (__a[2] as AccentRole) ?? 'base' };
    const __s = /^var\(--([a-z-]+)\)$/.exec(__v);
    if (__s && (SURFACE_ROLE_KEYS as readonly string[]).includes(__s[1])) return { kind: 'surface', role: __s[1] as SurfaceRole };
    return null;
}

// ─── SEMANTIC TOKENS ────────────────────────────────────────────────────────
// Role-based tokens. Colour defaults resolve to the accent/surface layer (wired in
// _semantic.scss); the admin re-points them per category via the base-color picker.
// Typography + shape + layout tokens stay admin-editable (Typography / Shape tabs).

export const SEMANTIC_TOKENS: Record<string, TokenEntry> = {
    // ── Surface ──────────────────────────────────────────────────────────
    'color.surface.bg': {
        cssVar: '--color-surface-bg',
        category: 'surface',
        label: 'Page background',
        description: 'Hoofd body-achtergrond. Volgt de body-context surface-rol (neutrale paginakleur, geen accent).',
        default: ctx('surface'),
        kind: 'color',
    },
    'color.surface.fg': {
        cssVar: '--color-surface-fg',
        category: 'surface',
        label: 'Page text',
        description: 'Tekstkleur op de body/surface. Volgt body on-surface, dus contrastvast t.o.v. de page background bij elk thema en color-mode.',
        default: ctx('on-surface'),
        kind: 'color',
    },
    'color.surface.area.bg': {
        cssVar: '--color-surface-area-bg',
        category: 'surface',
        label: 'Content area background',
        description: 'Achtergrond van een inhoudsgebied/sectie. Volgt body surface-container.',
        default: ctx('surface-container'),
        kind: 'color',
    },
    'color.surface.area.bg-hover': {
        cssVar: '--color-surface-area-bg-hover',
        category: 'surface',
        label: 'Content area hover background',
        description: 'Hover-tint voor klikbare gebieden (state-layer overlay).',
        default: '#0000000a',
        kind: 'color',
    },
    'color.surface.sidenav.bg': {
        cssVar: '--color-surface-sidenav-bg',
        category: 'surface',
        label: 'Side nav background',
        description: 'Achtergrond van de zij-navigatie. Volgt body surface-container-high.',
        default: ctx('surface-container-high'),
        kind: 'color',
    },
    'shape.surface.area.radius': {
        cssVar: '--shape-surface-area-radius',
        category: 'surface',
        label: 'Radius',
        description: 'Hoekafronding van content-secties (tablist-header, panels).',
        default: 'var(--radius-lg)',
        kind: 'length',
    },
    // ── Header (context: body) ──────────────────────────────────────────
    // bg + fg zijn losse, remappable tokens: default een contrastvast body-paar,
    // maar in de Semantic-tab onafhankelijk naar elke context-rol te herwijzen.
    'color.header.bg': {
        cssVar: '--color-header-bg',
        category: 'header',
        label: 'Background',
        description: 'Achtergrond van de hoofd-header. Volgt body surface-container-high.',
        default: ctx('surface-container-high'),
        kind: 'color',
    },
    'color.header.fg': {
        cssVar: '--color-header-fg',
        category: 'header',
        label: 'Text',
        description: 'Tekst-/iconkleur in de hoofd-header. Volgt body on-surface.',
        default: ctx('on-surface'),
        kind: 'color',
    },
    'shape.header.radius': {
        cssVar: '--shape-header-radius',
        category: 'header',
        label: 'Radius',
        description: 'Hoekafronding van de hoofd-header.',
        default: 'var(--radius-none)',
        kind: 'length',
    },

    // ── Footer (context: body) ──────────────────────────────────────────
    // Spiegel van de hoofd-header: bg + fg losse, remappable tokens (default een
    // contrastvast body-paar, in de Semantic-tab onafhankelijk te herwijzen).
    'color.footer.bg': {
        cssVar: '--color-footer-bg',
        category: 'footer',
        label: 'Background',
        description: 'Achtergrond van de hoofd-footer. Volgt body surface-container-high.',
        default: ctx('surface-container-high'),
        kind: 'color',
    },
    'color.footer.fg': {
        cssVar: '--color-footer-fg',
        category: 'footer',
        label: 'Text',
        description: 'Tekst-/iconkleur in de hoofd-footer. Volgt body on-surface.',
        default: ctx('on-surface'),
        kind: 'color',
    },
    'shape.footer.radius': {
        cssVar: '--shape-footer-radius',
        category: 'footer',
        label: 'Radius',
        description: 'Hoekafronding van de hoofd-footer.',
        default: 'var(--radius-none)',
        kind: 'length',
    },

    // ── Tablist (context: body) ─────────────────────────────────────────
    // Apart van de hoofd-header: eigen shape (top-only afronding) en kleur,
    // plus de afmetingen van de foto-tablist (image-box/-row, regelhoogte).
    'color.tablist-header.bg': {
        cssVar: '--color-tablist-header-bg',
        category: 'tablist',
        label: 'Header background',
        description: 'Achtergrond van de tablist-header. Volgt body surface-container.',
        default: ctx('surface-container'),
        kind: 'color',
    },
    'color.tablist-header.fg': {
        cssVar: '--color-tablist-header-fg',
        category: 'tablist',
        label: 'Header text',
        description: 'Tekst-/iconkleur in de tablist-header. Volgt body on-surface.',
        default: ctx('on-surface'),
        kind: 'color',
    },
    'shape.tablist-header.radius': {
        cssVar: '--shape-tablist-header-radius',
        category: 'tablist',
        label: 'Radius',
        description: 'Hoekafronding van de tablist-header (alleen bovenhoeken).',
        default: 'var(--radius-lg) var(--radius-lg) 0 0',
        kind: 'length',
    },
    'shape.tablist.image-box.size':    { cssVar: '--shape-tablist-image-box-size',    category: 'tablist', label: 'Image box size',    description: 'Afmeting van image-boxes in tab-list.', default: '6em', kind: 'length' },
    'shape.tablist.image-row.padding': { cssVar: '--shape-tablist-image-row-padding', category: 'tablist', label: 'Image row padding', description: 'Padding rond image-rows.', default: '0.75em', kind: 'length' },
    'shape.tablist.image-row.height':  { cssVar: '--shape-tablist-image-row-height',  category: 'tablist', label: 'Image row height',  description: 'Hoogte image-row (afgeleid op compile time).', default: '108px', kind: 'length' },
    'shape.tablist.line-height':       { cssVar: '--shape-tablist-line-height',       category: 'tablist', label: 'Line height',       description: 'Regelhoogte binnen tab-list.', default: '25px', kind: 'length' },

    // ── List items (context: body) ──────────────────────────────────────
    // Gedeelde look voor lijst-items en (app-breed) expansion-panels via de
    // list-item-surface mixin. bg/fg/bg-hover zijn losse, remappable tokens.
    'color.list-item.bg': {
        cssVar: '--color-list-item-bg',
        category: 'list-item',
        label: 'Background',
        description: 'Achtergrond van lijst-items. Default transparant (toont de onderliggende surface).',
        default: 'transparent',
        kind: 'color',
    },
    'color.list-item.fg': {
        cssVar: '--color-list-item-fg',
        category: 'list-item',
        label: 'Text',
        description: 'Tekstkleur van lijst-items. Volgt body on-surface.',
        default: ctx('on-surface'),
        kind: 'color',
    },
    'color.list-item.bg-hover': {
        cssVar: '--color-list-item-bg-hover',
        category: 'list-item',
        label: 'Hover background',
        description: 'Hover-achtergrond van lijst-items. Volgt body surface-container-high.',
        default: ctx('surface-container-high'),
        kind: 'color',
    },
    'color.list-item.active-bg': {
        cssVar: '--color-list-item-active-bg',
        category: 'list-item',
        label: 'Active background',
        description: 'Achtergrond van het actieve/geselecteerde lijst-item. Volgt body '
            + 'primary-container-high — een tier sterker dan de hover/surface zodat het '
            + 'actieve item duidelijk opvalt tegen de lijst-achtergrond.',
        default: ctx('primary-container-high'),
        kind: 'color',
    },
    'color.list-item.active-fg': {
        cssVar: '--color-list-item-active-fg',
        category: 'list-item',
        label: 'Active text',
        description: 'Tekstkleur van het actieve/geselecteerde lijst-item. Volgt body on-primary-container.',
        default: ctx('on-primary-container'),
        kind: 'color',
    },
    'color.list-item.border': {
        cssVar: '--color-list-item-border',
        category: 'list-item',
        label: 'Border',
        description: 'Randkleur van omkaderde lijst-blokken (bv. de navigatie-historie). Volgt body '
            + 'outline — dezelfde lijnkleur als invoervelden, maar als eigen knop instelbaar.',
        default: ctx('outline'),
        kind: 'color',
    },
    'shape.list-item.radius': {
        cssVar: '--shape-list-item-radius',
        category: 'list-item',
        label: 'Radius',
        description: 'Hoekafronding van lijst-items.',
        default: 'var(--radius-md)',
        kind: 'length',
    },
    'shape.list-item.size': {
        cssVar: '--shape-list-item-size',
        category: 'list-item',
        label: 'Size',
        description: 'Hoogte van lijst-items.',
        default: '40px',
        kind: 'length',
    },

    // ── Expansion list (context: body) ──────────────────────────────────
    // ExpansionListComponent: korte lijsten zonder virtual scroll, per rij een uitklapbaar paneel. De
    // rij zelf volgt de list-item-tokens; deze groep kleurt alleen het paneel en de uitklapknop.
    'color.expansion-list.panel-bg': {
        cssVar: '--color-expansion-list-panel-bg',
        category: 'expansion-list',
        label: 'Panel background',
        description: 'Achtergrond van een opengeklapte rij (kop + paneel). Volgt body surface-container.',
        default: ctx('surface-container'),
        kind: 'color',
    },
    'color.expansion-list.panel-fg': {
        cssVar: '--color-expansion-list-panel-fg',
        category: 'expansion-list',
        label: 'Panel text',
        description: 'Tekstkleur van de paneel-inhoud. Volgt body on-surface-variant.',
        default: ctx('on-surface-variant'),
        kind: 'color',
    },
    'color.expansion-list.panel-border': {
        cssVar: '--color-expansion-list-panel-border',
        category: 'expansion-list',
        label: 'Panel border',
        description: 'Scheidingslijn tussen kop en paneel-inhoud. Volgt body outline-variant.',
        default: ctx('outline-variant'),
        kind: 'color',
    },
    'color.expansion-list.toggle-fg': {
        cssVar: '--color-expansion-list-toggle-fg',
        category: 'expansion-list',
        label: 'Toggle',
        description: 'Kleur van de uitklapknop (chevron + aantal). Volgt body on-surface-variant.',
        default: ctx('on-surface-variant'),
        kind: 'color',
    },
    'shape.expansion-list.radius': {
        cssVar: '--shape-expansion-list-radius',
        category: 'expansion-list',
        label: 'Radius',
        description: 'Hoekafronding van een opengeklapte rij.',
        default: 'var(--radius-md)',
        kind: 'length',
    },
    // Uitklapanimatie (expandPanelAnimation): Angular-animaties lezen geen CSS-variabelen, dus de
    // lijst leest deze waarden bij elk open-/dichtklappen uit en geeft ze als animatie-params door.
    'shape.expansion-list.duration': {
        cssVar: '--shape-expansion-list-duration',
        category: 'expansion-list',
        label: 'Animation duration',
        description: 'Duur van het open- en dichtklappen, in ms of s (bv. 200ms; 0ms = geen animatie). '
            + 'Leeg = de gedeelde animatieduur uit Layout.',
        default: 'var(--layout-motion-duration)',
        kind: 'motion',
    },
    'shape.expansion-list.easing': {
        cssVar: '--shape-expansion-list-easing',
        category: 'expansion-list',
        label: 'Animation easing',
        description: 'Verloop van de uitklapanimatie: ease, ease-in, ease-out, ease-in-out, linear of cubic-bezier(...).',
        default: 'cubic-bezier(0.4, 0, 0.2, 1)',
        kind: 'motion',
    },

    // ── List position (context: body) ───────────────────────────────────
    // ListPositionComponent: transparant pilletje "nr - totaal" over een lijst tijdens het scrollen.
    // Omgekeerde kleuren (donker op licht thema, licht op donker) zodat het boven elke rij leesbaar is;
    // de transparantie zit in de component-styling, niet in de kleur.
    'color.list-position.bg': {
        cssVar: '--color-list-position-bg',
        category: 'list-position',
        label: 'Background',
        description: 'Achtergrond van de scrollpositie-aanduiding (half-transparant getoond). Volgt body on-surface.',
        default: ctx('on-surface'),
        kind: 'color',
    },
    'color.list-position.fg': {
        cssVar: '--color-list-position-fg',
        category: 'list-position',
        label: 'Text',
        description: 'Tekstkleur van de scrollpositie-aanduiding. Volgt body surface.',
        default: ctx('surface'),
        kind: 'color',
    },
    'shape.list-position.radius': {
        cssVar: '--shape-list-position-radius',
        category: 'list-position',
        label: 'Radius',
        description: 'Hoekafronding van de scrollpositie-aanduiding.',
        default: 'var(--radius-pill)',
        kind: 'length',
    },

    // ── Tooltip (context: body) ─────────────────────────────────────────
    // Material-tooltips (matTooltip), app-breed: gekoppeld in _m3-theme.scss naar --mat-tooltip-*. Omgekeerde
    // kleuren zoals de scrollpositie-aanduiding (donker op licht thema, licht op donker).
    'color.tooltip.bg': {
        cssVar: '--color-tooltip-bg',
        category: 'tooltip',
        label: 'Background',
        description: 'Achtergrond van een tooltip. Volgt body on-surface (omgekeerd t.o.v. de pagina).',
        default: ctx('on-surface'),
        kind: 'color',
    },
    'color.tooltip.fg': {
        cssVar: '--color-tooltip-fg',
        category: 'tooltip',
        label: 'Text',
        description: 'Tekstkleur van een tooltip. Volgt body surface.',
        default: ctx('surface'),
        kind: 'color',
    },
    'shape.tooltip.radius': {
        cssVar: '--shape-tooltip-radius',
        category: 'tooltip',
        label: 'Radius',
        description: 'Hoekafronding van een tooltip.',
        default: 'var(--radius-sm)',
        kind: 'length',
    },
    'shape.tooltip.font-size': {
        cssVar: '--shape-tooltip-font-size',
        category: 'tooltip',
        label: 'Text size',
        description: 'Tekstgrootte van een tooltip.',
        default: '12px',
        kind: 'length',
    },

    // ── Chat ──────────────────────────────────────────────────────────────
    // Chatpagina (berichtenstroom) en het aantal ongelezen in de chatlijst/header.
    'color.chat.badge.bg': {
        cssVar: '--color-chat-badge-bg',
        category: 'chat',
        label: 'Unread background',
        description: 'Achtergrond van het aantal ongelezen berichten (chatlijst, header). Volgt primary.',
        default: ctx('primary'),
        kind: 'color',
    },
    'color.chat.badge.fg': {
        cssVar: '--color-chat-badge-fg',
        category: 'chat',
        label: 'Unread text',
        description: 'Tekst van het aantal ongelezen berichten. Volgt on-primary.',
        default: ctx('on-primary'),
        kind: 'color',
    },
    'color.chat.mine.bg': {
        cssVar: '--color-chat-mine-bg',
        category: 'chat',
        label: 'Own message background',
        description: 'Achtergrond van een eigen bericht. Volgt primary-container.',
        default: ctx('primary-container'),
        kind: 'color',
    },
    'color.chat.mine.fg': {
        cssVar: '--color-chat-mine-fg',
        category: 'chat',
        label: 'Own message text',
        description: 'Tekst van een eigen bericht. Volgt on-primary-container.',
        default: ctx('on-primary-container'),
        kind: 'color',
    },
    'color.chat.other.bg': {
        cssVar: '--color-chat-other-bg',
        category: 'chat',
        label: 'Message background',
        description: 'Achtergrond van een bericht van een ander. Volgt surface-container-high.',
        default: ctx('surface-container-high'),
        kind: 'color',
    },
    'color.chat.other.fg': {
        cssVar: '--color-chat-other-fg',
        category: 'chat',
        label: 'Message text',
        description: 'Tekst van een bericht van een ander. Volgt on-surface.',
        default: ctx('on-surface'),
        kind: 'color',
    },
    'color.chat.separator.fg': {
        cssVar: '--color-chat-separator-fg',
        category: 'chat',
        label: 'Date separator',
        description: 'Tekst van de datumscheider tussen berichten. Volgt on-surface-variant.',
        default: ctx('on-surface-variant'),
        kind: 'color',
    },
    'shape.chat.bubble.radius': {
        cssVar: '--shape-chat-bubble-radius',
        category: 'chat',
        label: 'Message radius',
        description: 'Hoekafronding van een bericht.',
        default: 'var(--radius-lg)',
        kind: 'length',
    },

    // ── Task (taakmodule) ───────────────────────────────────────────────
    // Beoordelingsgeschiedenis (fase 4a) en het importvoorbeeld (fase 4c).
    'color.task.review.separator': {
        cssVar: '--color-task-review-separator',
        category: 'task',
        label: 'History separator',
        description: 'Scheidingslijn tussen regels in de beoordelingsgeschiedenis. Volgt outline-variant.',
        default: ctx('outline-variant'),
        kind: 'color',
    },
    'color.task.result.nok.fg': {
        cssVar: '--color-task-result-nok-fg',
        category: 'task',
        label: 'NOK text',
        description: 'Tekst van een oordeel NOK in de geschiedenis. Volgt warning.',
        default: ctx('warning'),
        kind: 'color',
    },
    'color.task.import.error.fg': {
        cssVar: '--color-task-import-error-fg',
        category: 'task',
        label: 'Import error',
        description: 'Tekst van een fout in het importvoorbeeld (regel wordt niet geïmporteerd). Volgt error.',
        default: ctx('error'),
        kind: 'color',
    },
    'color.task.import.warning.fg': {
        cssVar: '--color-task-import-warning-fg',
        category: 'task',
        label: 'Import warning',
        description: 'Tekst van een waarschuwing in het importvoorbeeld. Volgt warning.',
        default: ctx('warning'),
        kind: 'color',
    },

    // ── Notification (status colors) ────────────────────────────────────
    // Meldingen in de snackbar (NotificationComponent): per type (info/success/warning/error) een eigen
    // kaartje in de container-tint van de bijbehorende statuskleur; meerdere meldingen tegelijk tonen zo
    // elk hun eigen kleur.
    'color.notification.info.bg': {
        cssVar: '--color-notification-info-bg',
        category: 'notification',
        label: 'Info background',
        description: 'Achtergrond van een info-melding in de snackbar. Volgt info-container.',
        default: ctx('info-container'),
        kind: 'color',
    },
    'color.notification.info.fg': {
        cssVar: '--color-notification-info-fg',
        category: 'notification',
        label: 'Info text',
        description: 'Tekst-/iconkleur van een info-melding in de snackbar. Volgt on-info-container.',
        default: ctx('on-info-container'),
        kind: 'color',
    },
    'color.notification.success.bg': {
        cssVar: '--color-notification-success-bg',
        category: 'notification',
        label: 'Success background',
        description: 'Achtergrond van een success-melding in de snackbar. Volgt success-container.',
        default: ctx('success-container'),
        kind: 'color',
    },
    'color.notification.success.fg': {
        cssVar: '--color-notification-success-fg',
        category: 'notification',
        label: 'Success text',
        description: 'Tekst-/iconkleur van een success-melding in de snackbar. Volgt on-success-container.',
        default: ctx('on-success-container'),
        kind: 'color',
    },
    'color.notification.warning.bg': {
        cssVar: '--color-notification-warning-bg',
        category: 'notification',
        label: 'Warning background',
        description: 'Achtergrond van een warning-melding in de snackbar. Volgt warning-container.',
        default: ctx('warning-container'),
        kind: 'color',
    },
    'color.notification.warning.fg': {
        cssVar: '--color-notification-warning-fg',
        category: 'notification',
        label: 'Warning text',
        description: 'Tekst-/iconkleur van een warning-melding in de snackbar. Volgt on-warning-container.',
        default: ctx('on-warning-container'),
        kind: 'color',
    },
    'color.notification.error.bg': {
        cssVar: '--color-notification-error-bg',
        category: 'notification',
        label: 'Error background',
        description: 'Achtergrond van een error-melding in de snackbar. Volgt error-container.',
        default: ctx('error-container'),
        kind: 'color',
    },
    'color.notification.error.fg': {
        cssVar: '--color-notification-error-fg',
        category: 'notification',
        label: 'Error text',
        description: 'Tekst-/iconkleur van een error-melding in de snackbar. Volgt on-error-container.',
        default: ctx('on-error-container'),
        kind: 'color',
    },
    'shape.notification.radius': {
        cssVar: '--shape-notification-radius',
        category: 'notification',
        label: 'Radius',
        description: 'Hoekafronding van een melding-kaartje in de snackbar.',
        default: 'var(--radius-md)',
        kind: 'length',
    },

    // ── Action: Primary (context: button-action) ────────────────────────
    'color.button.action.bg': {
        cssVar: '--color-button-action-bg',
        category: 'button-action',
        label: 'Background',
        description: 'Achtergrond van hoofdactie-knoppen (CTA). Volgt button-action primary.',
        default: ctx('primary'),
        kind: 'color',
    },
    'color.button.action.fg': {
        cssVar: '--color-button-action-fg',
        category: 'button-action',
        label: 'Text',
        description: 'Tekstkleur op hoofdactie-knoppen. Volgt button-action on-primary.',
        default: ctx('on-primary'),
        kind: 'color',
    },
    'color.button.action.bg-hover': {
        cssVar: '--color-button-action-bg-hover',
        category: 'button-action',
        label: 'Hover background',
        description: 'Hover-achtergrond van hoofdactie-knoppen. Volgt button-action primary-container.',
        default: ctx('primary-container'),
        kind: 'color',
    },
    'color.button.action.ripple': {
        cssVar: '--color-button-action-ripple',
        category: 'button-action',
        label: 'Ripple',
        description: 'Material ripple-kleur op hoofdactie-knoppen. Volgt button-action primary.',
        default: ctx('primary'),
        kind: 'color',
    },
    'shape.button.action.radius': {
        cssVar: '--shape-button-action-radius',
        category: 'button-action',
        label: 'Radius',
        description: 'Hoekafronding van hoofdactie-knoppen. Default volgt --radius-lg.',
        default: 'var(--radius-lg)',
        kind: 'length',
    },
    'shape.button.action.border-width': {
        cssVar: '--shape-button-action-border-width',
        category: 'button-action',
        label: 'Border width',
        description: 'Randbreedte van hoofdactie-knoppen.',
        default: 'var(--border-width-none)',
        kind: 'length',
    },
    'shape.button.action.margin-between': {
        cssVar: '--shape-button-action-margin-between',
        category: 'button-action',
        label: 'Margin between',
        description: 'Ruimte tussen action buttons.',
        default: '0.25em',
        kind: 'length',
    },
    'shape.button.action.row-margin-block': {
        cssVar: '--shape-button-action-row-margin-block',
        category: 'button-action',
        label: 'Row margin block',
        description: 'Ruimte boven en onder een knoppenrij in de pagina-inhoud (app-buttons-row).',
        default: '0.5em',
        kind: 'length',
    },
    'shape.button.action.width': {
        cssVar: '--shape-button-action-width',
        category: 'button-action',
        label: 'Width',
        description: 'Minimale breedte van action buttons; bij langere tekst groeit de knop mee.',
        default: '5em',
        kind: 'length',
    },
    'shape.button.action.padding-inline': {
        cssVar: '--shape-button-action-padding-inline',
        category: 'button-action',
        label: 'Padding inline',
        description: 'Ruimte links en rechts tussen de tekst en de rand van action buttons.',
        default: '1em',
        kind: 'length',
    },
    'shape.button.action.height': {
        cssVar: '--shape-button-action-height',
        category: 'button-action',
        label: 'Height',
        description: 'Hoogte van action buttons. Icon-only-knoppen worden vierkant (height × height).',
        default: '2.5em',
        kind: 'length',
    },
    'shape.button.action.font-size': {
        cssVar: '--shape-button-action-font-size',
        category: 'button-action',
        label: 'Size',
        description: 'Schaal van action buttons via font-size: tekst, icoon, hoogte, breedte en radius '
            + 'schalen mee. Gebruik em (bv. 1.2em) zodat de knop de context-typografie blijft volgen; '
            + 'px koppelt hem daarvan los. Per plek overschrijfbaar via CSS font-size of de [fontSize]-input.',
        default: '1em',
        kind: 'length',
    },

    // ── Action: Navigation rail / chrome (context: button-navigation) ───
    'color.button.navigation.bg': {
        cssVar: '--color-button-navigation-bg',
        category: 'button-navigation',
        label: 'Background',
        description: 'Achtergrond van navigatie-rail knoppen.',
        default: 'transparent',
        kind: 'color',
    },
    'color.button.navigation.fg': {
        cssVar: '--color-button-navigation-fg',
        category: 'button-navigation',
        label: 'Icon / text',
        description: 'Icon-/tekstkleur van navigatie-knoppen. Volgt button-navigation primary.',
        default: ctx('primary'),
        kind: 'color',
    },
    'color.button.navigation.bg-hover': {
        cssVar: '--color-button-navigation-bg-hover',
        category: 'button-navigation',
        label: 'Hover background',
        description: 'Hover-achtergrond van navigatie-knoppen. Volgt button-navigation primary-container.',
        default: ctx('primary-container'),
        kind: 'color',
    },
    'color.button.navigation.ripple': {
        cssVar: '--color-button-navigation-ripple',
        category: 'button-navigation',
        label: 'Ripple',
        description: 'Ripple-kleur op navigatie-knoppen. Volgt button-navigation primary-container.',
        default: ctx('primary-container'),
        kind: 'color',
    },
    'color.button.navigation.border': {
        cssVar: '--color-button-navigation-border',
        category: 'button-navigation',
        label: 'Border',
        description: 'Randkleur van navigatie-knoppen. Volgt button-navigation outline-variant.',
        default: ctx('outline-variant'),
        kind: 'color',
    },
    'shape.button.navigation.radius': {
        cssVar: '--shape-button-navigation-radius',
        category: 'button-navigation',
        label: 'Radius',
        description: 'Hoekafronding van navigatie-knoppen.',
        default: 'var(--radius-pill)',
        kind: 'length',
    },
    'shape.button.navigation.border-width': {
        cssVar: '--shape-button-navigation-border-width',
        category: 'button-navigation',
        label: 'Border width',
        description: 'Randbreedte van navigatie-knoppen. In em, zodat hij meeschaalt met Size.',
        default: '0.2em',
        kind: 'length',
    },
    'shape.button.navigation.font-size': {
        cssVar: '--shape-button-navigation-font-size',
        category: 'button-navigation',
        label: 'Size',
        description: 'Schaal van navigatie-knoppen via font-size: de 3em-box, het icoon en de rand '
            + 'schalen mee. Gebruik em zodat de knop de context-typografie blijft volgen. Per plek '
            + 'overschrijfbaar via CSS font-size of de [fontSize]-input.',
        default: '1em',
        kind: 'length',
    },

    // ── Action: Neutral bare buttons (context: generic) ─────────────────
    'color.button.neutral.bg': {
        cssVar: '--color-button-neutral-bg',
        category: 'button-neutral',
        label: 'Background',
        description: 'Achtergrond van neutrale (niet-gebrande) knoppen.',
        default: 'transparent',
        kind: 'color',
    },
    'color.button.neutral.fg': {
        cssVar: '--color-button-neutral-fg',
        category: 'button-neutral',
        label: 'Text',
        description: 'Tekstkleur van neutrale knoppen. Volgt generic primary.',
        default: ctx('primary'),
        kind: 'color',
    },
    'color.button.neutral.bg-hover': {
        cssVar: '--color-button-neutral-bg-hover',
        category: 'button-neutral',
        label: 'Hover background',
        description: 'Hover-achtergrond van neutrale knoppen. Volgt generic primary-container.',
        default: ctx('primary-container'),
        kind: 'color',
    },
    'shape.button.neutral.radius': {
        cssVar: '--shape-button-neutral-radius',
        category: 'button-neutral',
        label: 'Radius',
        description: 'Hoekafronding van neutrale knoppen.',
        default: 'var(--radius-md)',
        kind: 'length',
    },
    'shape.button.neutral.border-width': {
        cssVar: '--shape-button-neutral-border-width',
        category: 'button-neutral',
        label: 'Border width',
        description: 'Randbreedte van neutrale knoppen.',
        default: 'var(--border-width-none)',
        kind: 'length',
    },
    'shape.button.neutral.font-size': {
        cssVar: '--shape-button-neutral-font-size',
        category: 'button-neutral',
        label: 'Size',
        description: 'Schaal van neutrale knoppen via font-size: tekst, icoon en radius schalen mee. '
            + 'Gebruik em zodat de knop de context-typografie blijft volgen. Per plek overschrijfbaar '
            + 'via CSS font-size of de [fontSize]-input.',
        default: '1em',
        kind: 'length',
    },

    // ── Action: Mini-FAB toolbar (context: generic) ─────────────────────
    'color.button.mini-fab.bg': {
        cssVar: '--color-button-mini-fab-bg',
        category: 'button-mini-fab',
        label: 'Background',
        description: 'Achtergrond van mini-FAB knoppen (o.a. tablist-footer). Volgt generic primary.',
        default: ctx('primary'),
        kind: 'color',
    },
    'color.button.mini-fab.fg': {
        cssVar: '--color-button-mini-fab-fg',
        category: 'button-mini-fab',
        label: 'Icon / text',
        description: 'Icon-/tekstkleur op mini-FAB knoppen. Volgt generic on-primary.',
        default: ctx('on-primary'),
        kind: 'color',
    },
    'color.button.mini-fab.bg-hover': {
        cssVar: '--color-button-mini-fab-bg-hover',
        category: 'button-mini-fab',
        label: 'Hover background',
        description: 'Hover-achtergrond van mini-FAB knoppen. Volgt generic primary-container.',
        default: ctx('primary-container'),
        kind: 'color',
    },
    'color.button.mini-fab.ripple': {
        cssVar: '--color-button-mini-fab-ripple',
        category: 'button-mini-fab',
        label: 'Ripple',
        description: 'Material ripple-kleur op mini-FAB knoppen. Volgt generic primary.',
        default: ctx('primary'),
        kind: 'color',
    },
    'shape.button.mini-fab.radius': {
        cssVar: '--shape-button-mini-fab-radius',
        category: 'button-mini-fab',
        label: 'Radius',
        description: 'Hoekafronding van mini-FAB knoppen. Default --radius-pill (circulair).',
        default: 'var(--radius-pill)',
        kind: 'length',
    },
    'shape.button.mini-fab.border-width': {
        cssVar: '--shape-button-mini-fab-border-width',
        category: 'button-mini-fab',
        label: 'Border width',
        description: 'Randbreedte van mini-FAB knoppen.',
        default: 'var(--border-width-none)',
        kind: 'length',
    },
    'shape.button.mini-fab.font-size': {
        cssVar: '--shape-button-mini-fab-font-size',
        category: 'button-mini-fab',
        label: 'Size',
        description: 'Schaal van mini-FAB knoppen via font-size: box en glyph schalen mee. Een per-plek '
            + 'gezette --mini-fab-size heeft voorrang op de box-maat. Gebruik em zodat de knop de '
            + 'context-typografie blijft volgen. Per plek overschrijfbaar via CSS font-size of de [fontSize]-input.',
        default: '1em',
        kind: 'length',
    },

    // ── Action: Flat toggle buttons (context: generic) ──────────────────
    'color.button.flat.bg': {
        cssVar: '--color-button-flat-bg',
        category: 'button-flat',
        label: 'Background',
        description: 'Achtergrond van flat (omrande) knoppen.',
        default: 'transparent',
        kind: 'color',
    },
    'color.button.flat.fg': {
        cssVar: '--color-button-flat-fg',
        category: 'button-flat',
        label: 'Text',
        description: 'Tekstkleur van flat knoppen. Volgt generic primary.',
        default: ctx('primary'),
        kind: 'color',
    },
    'color.button.flat.bg-hover': {
        cssVar: '--color-button-flat-bg-hover',
        category: 'button-flat',
        label: 'Hover background',
        description: 'Hover-achtergrond van flat knoppen. Volgt generic primary-container.',
        default: ctx('primary-container'),
        kind: 'color',
    },
    'color.button.flat.border': {
        cssVar: '--color-button-flat-border',
        category: 'button-flat',
        label: 'Border',
        description: 'Randkleur van flat knoppen. Volgt generic outline-variant.',
        default: ctx('outline-variant'),
        kind: 'color',
    },
    'shape.button.flat.radius': {
        cssVar: '--shape-button-flat-radius',
        category: 'button-flat',
        label: 'Radius',
        description: 'Hoekafronding van flat knoppen.',
        default: 'var(--radius-sm)',
        kind: 'length',
    },
    'shape.button.flat.border-width': {
        cssVar: '--shape-button-flat-border-width',
        category: 'button-flat',
        label: 'Border width',
        description: 'Randbreedte van flat knoppen.',
        default: 'var(--border-width-thin)',
        kind: 'length',
    },
    'shape.button.flat.font-size': {
        cssVar: '--shape-button-flat-font-size',
        category: 'button-flat',
        label: 'Size',
        description: 'Schaal van flat knoppen via font-size. Gebruik em zodat de knop de '
            + 'context-typografie blijft volgen. Per plek overschrijfbaar via CSS font-size.',
        default: '1em',
        kind: 'length',
    },

    // ── Action: Icon buttons (context: generic) ─────────────────────────
    'color.button.icon.bg': {
        cssVar: '--color-button-icon-bg',
        category: 'button-icon',
        label: 'Background',
        description: 'Achtergrond van icoon-knoppen.',
        default: 'transparent',
        kind: 'color',
    },
    'color.button.icon.fg': {
        cssVar: '--color-button-icon-fg',
        category: 'button-icon',
        label: 'Icon',
        description: 'Icoonkleur van icoon-knoppen. Default `inherit` (icoon neemt de '
            + 'kleur van zijn omgeving over); per thema overschrijfbaar.',
        default: 'inherit',
        kind: 'color',
    },
    'color.button.icon.bg-hover': {
        cssVar: '--color-button-icon-bg-hover',
        category: 'button-icon',
        label: 'Hover background',
        description: 'Hover-achtergrond van icoon-knoppen. Volgt generic primary-container.',
        default: ctx('primary-container'),
        kind: 'color',
    },
    'shape.button.icon.radius': {
        cssVar: '--shape-button-icon-radius',
        category: 'button-icon',
        label: 'Radius',
        description: 'Hoekafronding van icoon-knoppen. Default --radius-pill (rond), zodat de '
            + 'ripple/state-layer (die deze radius erft) circulair clipt i.p.v. vierkant.',
        default: 'var(--radius-pill)',
        kind: 'length',
    },
    'shape.button.icon.border-width': {
        cssVar: '--shape-button-icon-border-width',
        category: 'button-icon',
        label: 'Border width',
        description: 'Randbreedte van icoon-knoppen.',
        default: 'var(--border-width-none)',
        kind: 'length',
    },
    'shape.button.icon.ripple-size': {
        cssVar: '--shape-button-icon-ripple-size',
        category: 'button-icon',
        label: 'Ripple size',
        description: 'Omvang van box + ripple (Material state-layer) van icoon-knoppen; het icoon zelf '
            + 'is 60% hiervan. Een per-plek gezette --btn-icon-size heeft voorrang.',
        default: '2em',
        kind: 'length',
    },
    'shape.button.icon.font-size': {
        cssVar: '--shape-button-icon-font-size',
        category: 'button-icon',
        label: 'Size',
        description: 'Schaal van icoon-knoppen via font-size: box, ripple en icoon schalen mee '
            + '(bovenop Ripple size). Gebruik em zodat de knop de context-typografie blijft volgen. '
            + 'Per plek overschrijfbaar via CSS font-size of de [fontSize]-input.',
        default: '1em',
        kind: 'length',
    },

    // ── Button disabled (gedeeld door alle knop-varianten) ───────────────
    // Disabled = een lichtere tint van de eigen knopkleur (recht wel, maar geblokkeerd — zie
    // structure-locked / "niets te bewerken"): de knop-mixin mengt de eigen bg/fg met deze tint
    // (verhouding $disabled-own in _button-mixins.scss). Transparante knoppen krijgen lagere dekking.
    'color.button.disabled.tint': {
        cssVar: '--color-button-disabled-tint',
        category: 'button-disabled',
        label: 'Tint',
        description: 'Kleur waarmee disabled knoppen hun eigen kleur mengen (lichter maken). Volgt generic surface.',
        default: ctx('surface'),
        kind: 'color',
    },
    // ── Input / form controls (context: control) ────────────────────────
    'color.input.bg': {
        cssVar: '--color-input-bg',
        category: 'input',
        label: 'Background',
        description: 'Achtergrond van bewerkbare formulier-controls (edit-modus). Volgt control '
            + 'surface-container-lowest (wit in light), zodat bewerkbaar zich onderscheidt van readonly.',
        default: ctx('surface-container-lowest'),
        kind: 'color',
    },
    'color.input.bg-readonly': {
        cssVar: '--color-input-bg-readonly',
        category: 'input',
        label: 'Background readonly',
        description: 'Achtergrond van alleen-lezen formulier-controls (details, of niet-bewerkbaar '
            + 'veld in edit-modus). Volgt control surface-container.',
        default: ctx('surface-container'),
        kind: 'color',
    },
    'color.input.fg': {
        cssVar: '--color-input-fg',
        category: 'input',
        label: 'Text / icon',
        description: 'Tekst-/iconkleur in formulier-controls. Volgt control on-surface.',
        default: ctx('on-surface'),
        kind: 'color',
    },
    'color.input.selected-bg': {
        cssVar: '--color-input-selected-bg',
        category: 'input',
        label: 'Selected background',
        description: 'Kleur van de gekozen stand in keuze-controls: de geselecteerde knop van een '
            + 'button-toggle en de track van een slide-toggle in de aan-stand. Volgt primary.',
        default: ctx('primary'),
        kind: 'color',
    },
    'color.input.selected-fg': {
        cssVar: '--color-input-selected-fg',
        category: 'input',
        label: 'Selected text / icon',
        description: 'Tekst/icoon op Selected background: label van de geselecteerde toggle-knop en '
            + 'de handle (bolletje) van een slide-toggle in de aan-stand. Volgt on-primary.',
        default: ctx('on-primary'),
        kind: 'color',
    },
    'color.input.placeholder': {
        cssVar: '--color-input-placeholder',
        category: 'input',
        label: 'Placeholder',
        description: 'Placeholder-tekst in formulier-controls; bewust lichter dan Text zodat een '
            + 'placeholder niet voor ingevoerde tekst wordt aangezien. Volgt control outline.',
        default: ctx('outline'),
        kind: 'color',
    },
    'color.input.border': {
        cssVar: '--color-input-border',
        category: 'input',
        label: 'Border',
        description: 'Randkleur van formulier-controls. Volgt control outline.',
        default: ctx('outline'),
        kind: 'color',
    },
    'shape.input.radius': {
        cssVar: '--shape-input-radius',
        category: 'input',
        label: 'Radius',
        description: 'Hoekafronding van formulier-controls.',
        default: 'var(--radius-sm)',
        kind: 'length',
    },
    'shape.input.border-width': {
        cssVar: '--shape-input-border-width',
        category: 'input',
        label: 'Border width',
        description: 'Randbreedte van formulier-controls.',
        default: 'var(--border-width-thin)',
        kind: 'length',
    },

    // ── Image box (foto-/logo-kaders) ───────────────────────────────────
    'color.imagebox.bg': {
        cssVar: '--color-imagebox-bg',
        category: 'imagebox',
        label: 'Background',
        description: 'Achtergrond/vulling achter de afbeelding (zichtbaar bij lege of niet-vullende '
            + 'foto). Standaard transparant; instelbaar.',
        default: 'transparent',
        kind: 'color',
    },
    'color.imagebox.border': {
        cssVar: '--color-imagebox-border',
        category: 'imagebox',
        label: 'Border',
        description: 'Randkleur van omkaderde foto-/logo-boxen in rust. Volgt body outline.',
        default: ctx('outline'),
        kind: 'color',
    },
    'color.imagebox.color': {
        cssVar: '--color-imagebox-color',
        category: 'imagebox',
        label: 'Color (drag-doel rand)',
        description: 'Alternatieve randkleur die geldige sleep-doelen markeert tijdens drag-&-drop '
            + '(zo zie je waar je een foto naartoe kunt slepen). Standaard de primaire accentkleur.',
        default: ctx('primary'),
        kind: 'color',
    },
    'shape.imagebox.radius': {
        cssVar: '--shape-imagebox-radius',
        category: 'imagebox',
        label: 'Radius',
        description: 'Hoekafronding van foto-/logo-boxen.',
        default: 'var(--radius-sm)',
        kind: 'length',
    },
    'shape.imagebox.border-width': {
        cssVar: '--shape-imagebox-border-width',
        category: 'imagebox',
        label: 'Border width',
        description: 'Randbreedte van omkaderde foto-/logo-boxen.',
        default: 'var(--border-width-thin)',
        kind: 'length',
    },

    // ── Link (context: generic) ─────────────────────────────────────────
    'color.link.fg': {
        cssVar: '--color-link-fg',
        category: 'link',
        label: 'Color',
        description: 'Tekstkleur van links (bv. image-box link-overlays). Volgt generic tertiary.',
        default: ctx('tertiary'),
        kind: 'color',
    },
    'color.link.fg-hover': {
        cssVar: '--color-link-fg-hover',
        category: 'link',
        label: 'Hover color',
        description: 'Hover-tekstkleur van links. Volgt generic tertiary.',
        default: ctx('tertiary'),
        kind: 'color',
    },

    // ── Typography: Families ────────────────────────────────────────────
    'text.heading.family': {
        cssVar: '--text-heading-family',
        category: 'typography-family',
        label: 'Heading font',
        description: 'Lettertype voor h1–h6 koppen.',
        default: "Roboto, sans-serif",
        kind: 'fontFamily',
    },
    'text.body.family': {
        cssVar: '--text-body-family',
        category: 'typography-family',
        label: 'Body font',
        description: 'Lettertype voor lopende tekst.',
        default: "Roboto, sans-serif",
        kind: 'fontFamily',
    },
    'text.mono.family': {
        cssVar: '--text-mono-family',
        category: 'typography-family',
        label: 'Monospace font',
        description: 'Lettertype voor code/monospace tekst.',
        default: "'Roboto Mono', monospace",
        kind: 'fontFamily',
    },

    // ── Typography: Scale ───────────────────────────────────────────────
    'text.h1.size':   { cssVar: '--text-h1-size',   category: 'typography-scale', label: 'H1 size',   description: 'Tekstgrootte voor h1.', default: '34px', kind: 'length' },
    'text.h1.weight': { cssVar: '--text-h1-weight', category: 'typography-scale', label: 'H1 weight', description: 'Font-weight voor h1.', default: '500',  kind: 'number' },
    'text.h2.size':   { cssVar: '--text-h2-size',   category: 'typography-scale', label: 'H2 size',   description: 'Tekstgrootte voor h2.', default: '24px', kind: 'length' },
    'text.h2.weight': { cssVar: '--text-h2-weight', category: 'typography-scale', label: 'H2 weight', description: 'Font-weight voor h2.', default: '500',  kind: 'number' },
    'text.h3.size':   { cssVar: '--text-h3-size',   category: 'typography-scale', label: 'H3 size',   description: 'Tekstgrootte voor h3.', default: '16px', kind: 'length' },
    'text.h3.weight': { cssVar: '--text-h3-weight', category: 'typography-scale', label: 'H3 weight', description: 'Font-weight voor h3.', default: '500',  kind: 'number' },
    'text.body.size': { cssVar: '--text-body-size', category: 'typography-scale', label: 'Body size', description: 'Tekstgrootte voor lopende tekst.', default: '14px', kind: 'length' },
    'text.body.weight': { cssVar: '--text-body-weight', category: 'typography-scale', label: 'Body weight', description: 'Font-weight voor lopende tekst.', default: '400', kind: 'number' },
    'text.body.line': { cssVar: '--text-body-line', category: 'typography-scale', label: 'Body line height', description: 'Regelhoogte voor lopende tekst.', default: '1.5', kind: 'lineHeight' },
    'text.caption.size': { cssVar: '--text-caption-size', category: 'typography-scale', label: 'Caption size', description: 'Tekstgrootte voor caption/small.', default: '12px', kind: 'length' },

    // ── Motion (shape tab; gedeelde animatie-timing, los van één element) ──
    'layout.motion.duration': { cssVar: '--layout-motion-duration', category: 'layout', label: 'Animatieduur', description: 'Gedeelde duur voor UI-animaties (bv. uitklappen in de navigatie-historie).', default: '500ms', kind: 'length' },
    'layout.motion.duration-drag': { cssVar: '--layout-motion-duration-drag', category: 'layout', label: 'Animatieduur slepen', description: 'Duur waarmee items opzij schuiven tijdens drag-&-drop (bv. foto-tablist).', default: '250ms', kind: 'length' },

    // ── Layout (shape tab; geometry) ────────────────────────────────────
    'layout.content-inset.left':  { cssVar: '--layout-content-inset-left',  category: 'layout', label: 'Content inset left',  description: 'Linker-inset van inhoudsgebieden.',  default: '16px',  kind: 'length' },
    'layout.content-inset.right': { cssVar: '--layout-content-inset-right', category: 'layout', label: 'Content inset right', description: 'Rechter-inset van inhoudsgebieden.', default: 'calc(2.5em + var(--layout-scrollbar-width))', kind: 'length' },
    'layout.scrollbar.width':     { cssVar: '--layout-scrollbar-width',     category: 'layout', label: 'Scrollbar width',     description: 'Breedte van de scrollbar (0 op touch).', default: '17px', kind: 'length' },
    'layout.button.icon.margin-between':   { cssVar: '--layout-button-icon-margin-between',   category: 'layout', label: 'Icon btn margin between',   description: 'Ruimte tussen icon-buttons.', default: '0.25em', kind: 'length' },
    'layout.central-page.tab-section-with-photo.top':    { cssVar: '--layout-central-page-tab-section-with-photo-top',    category: 'layout', label: 'Central page tab section (with photo) top', description: '', default: '8em', kind: 'length' },
    'layout.central-page.details-section-photo.padding-top':    { cssVar: '--layout-central-page-details-section-photo-padding-top',    category: 'layout', label: 'Details section (with photo) padding-top', description: '', default: '.5em', kind: 'length' },
    'layout.central-page.details-section-no-photo.padding-top': { cssVar: '--layout-central-page-details-section-no-photo-padding-top', category: 'layout', label: 'Details section (no photo) padding-top',   description: '', default: '1em', kind: 'length' },
    'layout.side-box.padding':       { cssVar: '--layout-side-box-padding',       category: 'layout', label: 'Side box padding', description: '', default: '.25em', kind: 'length' },
    'layout.side-box.width':         { cssVar: '--layout-side-box-width',         category: 'layout', label: 'Side box width',   description: '', default: '3em',   kind: 'length' },
    'layout.side-container.width':   { cssVar: '--layout-side-container-width',   category: 'layout', label: 'Side container width', description: '', default: 'calc(var(--layout-side-box-width) - var(--layout-side-box-padding) * 2)', kind: 'length' },
    'layout.tab-page.section-height': { cssVar: '--layout-tab-page-section-height', category: 'layout', label: 'Tab page section height', description: '', default: 'auto', kind: 'length' },
};

export const PRIMITIVE_TOKENS = PRIMITIVES;

export type SemanticTokenName = keyof typeof SEMANTIC_TOKENS;
export type PrimitiveTokenName = keyof typeof PRIMITIVES;
export type TokenName = SemanticTokenName | PrimitiveTokenName;

/**
 * light/dark is the only runtime mode axis. Contrast/Dyslexic/Density are no
 * longer orthogonal toggles — they become separate theme records (Themes rows).
 * ThemeService writes colorMode as a data-attribute and, more importantly, feeds
 * it as isDark into the per-context HCT schemes.
 */
export const MODE_ATTRIBUTES = {
    colorMode: { attr: 'data-color-mode', values: ['light', 'dark'] as const, default: 'light' as const },
};

export type ColorMode = typeof MODE_ATTRIBUTES.colorMode.values[number];

/**
 * The user's choice: a fixed light/dark, or 'system' to follow the OS setting
 * (prefers-color-scheme). ThemeService resolves it to the applied ColorMode, so
 * everything downstream (generator, data-color-mode, theme editor) stays light/dark.
 */
export const colorModePreference = {
    light: 'light',
    dark: 'dark',
    system: 'system',
} as const;

export type ColorModePreference = (typeof colorModePreference)[keyof typeof colorModePreference];

/** Per-user, persisted theme preference (Phase 6: User table). */
export type ThemeSettings = {
    selectedThemeGuid: string | null;
    colorMode: ColorModePreference;
};

/**
 * A Theme as stored in the backend (frontend-owned JSON). The admin edits the 8
 * base colors, the per-category accent assignment, the surface source colour,
 * typography and shape. light/dark is derived per base color from HCT isDark.
 */

/** Per-category accent base color: which base color a category's bg/fg/hover follow. */
export type AccentColorMap = Partial<Record<TokenCategory, BaseColorKey>>;

/**
 * Per-category hover/container intensity override (0…100). A category present here
 * deviates from the theme-wide baseline `hoverIntensity`; absent = follow the baseline.
 * Lets co-tenants of one base color (e.g. button-action vs button-icon, both c1) carry
 * a different hover strength even though they share the same --accent-{key}-* family.
 */
export type HoverIntensityMap = Partial<Record<TokenCategory, number>>;

/**
 * Per-token colour override. The value is a nuance ref: an accent nuance of a base
 * color (`"c4:container"`, role ∈ ACCENT_ROLE_KEYS) or a shared surface nuance
 * (`"surface:outline"`, role ∈ SURFACE_ROLE_KEYS). Unset = follow the token's default.
 */
export type SemanticColorMap = Partial<Record<SemanticTokenName, string>>;

/** Resolve a per-token nuance ref to its CSS var. `"c4:container"` → var(--accent-c4-container);
 *  `"surface:outline"` → var(--outline). A bare/invalid ref is returned unchanged. */
export function tokenRefToVar(ref: string): string {
    const __i = ref.indexOf(':');
    if (__i < 0) return ref;
    const __head = ref.slice(0, __i);
    const __role = ref.slice(__i + 1);
    if (__head === 'surface') return surfaceVar(__role as SurfaceRole);
    return accentVar(__head as BaseColorKey, __role as AccentRole);
}

/** Whether a string is a syntactically valid nuance ref (head + known role). */
export function isTokenRef(ref: string): boolean {
    const __i = ref.indexOf(':');
    if (__i < 0) return false;
    const __head = ref.slice(0, __i);
    const __role = ref.slice(__i + 1);
    if (__head === 'surface') return (SURFACE_ROLE_KEYS as readonly string[]).includes(__role);
    return (BASE_COLOR_KEYS as readonly string[]).includes(__head) && (ACCENT_ROLE_KEYS as readonly string[]).includes(__role);
}

export type Theme = {
    id: string;
    name: string;
    isDefault: boolean;
    baseColors: BaseColors;
    // Per-category accent base color (bg/fg/hover of a component group). Sparse; a
    // missing category falls back to CATEGORY_ACCENT (or is surface-only).
    accentColors: AccentColorMap;
    // Base color that seeds the shared neutral/surface set (page bg, chrome, list bg).
    // Changing it re-tints every surface in proportion; default 'neutral'.
    surfaceColor: BaseColorKey;
    typography: Partial<Record<SemanticTokenName, string>>;
    shape: Partial<Record<SemanticTokenName, string>>;
    // Baseline hover/container intensity (0…100%, 100 = full M3 distance): how far the
    // accent *-container tones deviate from the base accent for categories without an
    // override. No longer a single editable global knob — it's the per-category fallback.
    hoverIntensity: number;
    // Per-category hover intensity override (0…100). Sparse; a missing category follows
    // the baseline hoverIntensity. Re-tints only its hover-tier tokens.
    hoverIntensities: HoverIntensityMap;
    // Per-token accent override (re-point one token at a base color). Sparse.
    // This is the LIGHT/base override: it applies in both modes unless overridden by
    // semanticColorsDark for the same token.
    semanticColors: SemanticColorMap;
    // Per-token override that only applies in DARK mode. Sparse; a token absent here
    // falls back to its semanticColors (light) entry, which is itself mode-aware via the
    // accent var indirection. Lets the admin pin a different nuance per mode when a manual
    // pick breaks the automatic on-color contrast.
    semanticColorsDark: SemanticColorMap;
    // Per-token transparency on the app-facing colour token (0…100%, unset = 100 = opaque).
    // Applied via color-mix over the token's resolved colour (mapping or generated default).
    semanticAlpha: Partial<Record<SemanticTokenName, number>>;
};

// ─── Factory default theme (mirrors the static SCSS fallback) ───────────────

export const DEFAULT_BASE_COLORS: BaseColors = {
    c1: '#03a9f4', // primary blue
    c2: '#46b4e0', // secondary cyan
    c3: '#ba1f1c', // tertiary red (links)
    c4: '#4caf50', // green   (spare)
    c5: '#ff9800', // orange  (spare)
    c6: '#9c27b0', // purple  (spare)
    neutral: '#9e9e9e',
    info:    '#2196f3', // status: informatie (blauw)
    success: '#43a047', // status: gelukt (groen)
    warning: '#fb8c00', // status: let op (amber/oranje)
    error:   '#f44336', // status: fout (rood)
};

export const DEFAULT_THEME: Theme = {
    id: 'default',
    name: 'Default',
    isDefault: true,
    baseColors: { ...DEFAULT_BASE_COLORS },
    accentColors: {},
    surfaceColor: 'neutral',
    typography: {},
    shape: {},
    hoverIntensity: 100,
    hoverIntensities: {},
    semanticColors: {},
    semanticColorsDark: {},
    semanticAlpha: {},
};

/**
 * Coerce a parsed (possibly legacy) theme JSON into the current Theme shape: fill
 * missing fields, coerce a legacy per-context hoverIntensity object to the global
 * number, drop legacy contexts/contextOverrides, and discard a legacy "ctx:role"
 * semanticColors map (values are BaseColorKey now). Tolerant of partial input.
 */
export function normalizeTheme(raw: unknown): Theme {
    const __r = (raw ?? {}) as Partial<Theme> & Record<string, unknown>;
    const __isKey = (v: unknown): v is BaseColorKey => typeof v === 'string' && (BASE_COLOR_KEYS as readonly string[]).includes(v);
    // semanticColors values are nuance refs now ("c4:container"); keep valid refs, and
    // upgrade a legacy bare base-color value ("c4") to its base nuance ("c4:base").
    const __normalizeRefMap = (raw: unknown): SemanticColorMap => {
        const __out: SemanticColorMap = {};
        for (const [__k, __v] of Object.entries((raw ?? {}) as Record<string, unknown>)) {
            if (typeof __v !== 'string') continue;
            if (isTokenRef(__v)) __out[__k as SemanticTokenName] = __v;
            else if (__isKey(__v)) __out[__k as SemanticTokenName] = `${__v}:base`;
        }
        return __out;
    };
    const __semanticColors = __normalizeRefMap(__r.semanticColors);
    const __semanticColorsDark = __normalizeRefMap(__r.semanticColorsDark);
    // Per-category hover overrides: keep only numeric entries (drop legacy/garbage).
    const __hoverIntensities: HoverIntensityMap = {};
    for (const [__k, __v] of Object.entries((__r.hoverIntensities ?? {}) as Record<string, unknown>)) {
        if (typeof __v === 'number') __hoverIntensities[__k as TokenCategory] = __v;
    }
    return {
        id: typeof __r.id === 'string' ? __r.id : DEFAULT_THEME.id,
        name: typeof __r.name === 'string' ? __r.name : DEFAULT_THEME.name,
        isDefault: !!__r.isDefault,
        baseColors: { ...DEFAULT_BASE_COLORS, ...(__r.baseColors as BaseColors | undefined) },
        accentColors: (__r.accentColors as AccentColorMap | undefined) ?? {},
        surfaceColor: __isKey(__r.surfaceColor) ? __r.surfaceColor : 'neutral',
        typography: (__r.typography as Theme['typography']) ?? {},
        shape: (__r.shape as Theme['shape']) ?? {},
        hoverIntensity: typeof __r.hoverIntensity === 'number' ? __r.hoverIntensity : 100,
        hoverIntensities: __hoverIntensities,
        semanticColors: __semanticColors,
        semanticColorsDark: __semanticColorsDark,
        semanticAlpha: (__r.semanticAlpha as Theme['semanticAlpha']) ?? {},
    };
}

// ─── Accent assignment + resolution helpers ─────────────────────────────────

/** The base color a category's accent tokens follow: override → catalog default → none. */
export function accentKeyForCategory(theme: Pick<Theme, 'accentColors'>, category: TokenCategory): BaseColorKey | undefined {
    return theme.accentColors?.[category] ?? CATEGORY_ACCENT[category];
}

/** A category's effective hover intensity (0…100): per-category override → global → 100. */
export function hoverForCategory(theme: Pick<Theme, 'hoverIntensity' | 'hoverIntensities'>, category: TokenCategory): number {
    const __override = theme.hoverIntensities?.[category];
    return typeof __override === 'number' ? __override : (theme.hoverIntensity ?? 100);
}

/** Categories that carry an accent (default or overridden) — drives the editor + reverse index. */
export function accentCategories(theme: Pick<Theme, 'accentColors'>): TokenCategory[] {
    const __set = new Set<TokenCategory>(Object.keys(CATEGORY_ACCENT) as TokenCategory[]);
    for (const __k of Object.keys(theme.accentColors ?? {})) __set.add(__k as TokenCategory);
    return [...__set];
}

/** Reverse index: base color → categories using it as accent ("used by" in the editor). */
export function accentUsage(theme: Pick<Theme, 'accentColors'>): Partial<Record<BaseColorKey, TokenCategory[]>> {
    const __out: Partial<Record<BaseColorKey, TokenCategory[]>> = {};
    for (const __cat of accentCategories(theme)) {
        const __key = accentKeyForCategory(theme, __cat);
        if (__key) (__out[__key] ??= []).push(__cat);
    }
    return __out;
}

/**
 * Resolve a colour token's CSS value for a theme. Precedence: a per-token nuance ref
 * (semanticColors, e.g. "c4:container") wins; else an accent token follows its category
 * accent (keeping its role); else surface/literal tokens keep their default (surface
 * stays the globally re-tintable var).
 */
export function resolveTokenColor(
    theme: Pick<Theme, 'accentColors' | 'semanticColors'> & Partial<Pick<Theme, 'semanticColorsDark'>>,
    name: SemanticTokenName, entry: TokenEntry, isDark = false,
): string {
    // Dark mode: a per-token dark override wins; otherwise fall back to the light override.
    const __override = (isDark ? theme.semanticColorsDark?.[name] : undefined) ?? theme.semanticColors?.[name];
    if (__override) return tokenRefToVar(__override);
    const __parsed = parseTokenColor(entry.default);
    if (__parsed && __parsed.kind === 'accent') {
        return accentVar(accentKeyForCategory(theme, entry.category) ?? __parsed.key, __parsed.role);
    }
    return entry.default;
}

// ─── Semantic colour re-mapping helpers ─────────────────────────────────────
// The app-facing colour tokens an admin may re-point to a different base color.
// Data-driven by category: add a new color token in one of these categories and it
// shows up in the Elements tab automatically.
// 'notification' bewust zonder CATEGORY_ACCENT: één accent-picker per categorie zou alle
// meldingstypes dezelfde kleur geven; de admin past ze per token aan.
export const REMAPPABLE_COLOR_CATEGORIES: ReadonlySet<TokenCategory> = new Set<TokenCategory>([
    'surface', 'header', 'tablist', 'footer', 'list-item', 'button-action', 'button-flat', 'button-navigation', 'button-neutral',
    'button-mini-fab', 'button-icon', 'button-disabled', 'input', 'link', 'imagebox', 'notification', 'tooltip', 'chat',
]);

export function remappableColorTokens(): { name: SemanticTokenName; entry: TokenEntry }[] {
    return (Object.entries(SEMANTIC_TOKENS) as [SemanticTokenName, TokenEntry][])
        .filter(([, e]) => e.kind === 'color' && REMAPPABLE_COLOR_CATEGORIES.has(e.category))
        .map(([name, entry]) => ({ name, entry }));
}

/** Overlay an alpha percentage (0…100) onto a `#RRGGBB` hex → `#RRGGBBAA`. Non-6-digit
 * hex (already has alpha, or a var()) is returned unchanged; 100% is a no-op. */
export function hexWithAlpha(hex: string, pct: number): string {
    if (pct >= 100 || !/^#[0-9a-fA-F]{6}$/.test(hex)) return hex;
    const __byte = Math.round(Math.max(0, Math.min(100, pct)) / 100 * 255);
    return hex + __byte.toString(16).padStart(2, '0');
}
